/**
 * components/onboarding/wizardSteps.ts
 *
 * Wizard v3 step model. Question ownership lives in
 * `strategy-section-registry.ts` so the wizard and the strategy editor prompt
 * cannot disagree about which question configures which section.
 *
 * A section's questions are asked **on the settings step itself**, above the
 * controls they set, so an answer and the number it produces are never more
 * than a scroll apart. There are no question-screen steps left at all: the
 * behavioural questionnaire is gone — the profile is picked outright on the
 * setup choice — and the one question still asked (the expected prices, on
 * Key Metrics) is an inline prompt. See `RETIRED_STEP_KEYS` for how a profile
 * saved on one of the retired screens resumes, and `resumeStepKeyForProfile`
 * for one saved on a screen that has since moved further down the list.
 *
 * Channel distribution mapping and non-refundable pricing are intentionally not
 * part of the wizard — they are optional, advanced settings configured later in
 * the strategy editor.
 *
 * Steps marked `tier: "expert"` ship with defaults that already price correctly,
 * so they are walked past unless the operator has switched expert mode on —
 * and the wizard does not offer expert mode at all for now
 * (`WIZARD_EXPERT_MODE_AVAILABLE`).
 *
 * `expertFields` marks an essential step that still holds expert-tier controls
 * inside it. Those steps carry their own reveal, so wanting one advanced dial
 * costs the operator that step rather than the whole essential path.
 */

import {
    STRATEGY_SECTION_REGISTRY,
    sectionForQuestion,
    strategySection,
    type StrategySectionId,
} from "../pricing/strategy-section-registry";
import { isSettingVisible, type SettingsTier } from "../pricing/settings-tier";
import type { StrategySectionGroup } from "../pricing/strategy-section-groups";
import { isSectionMeasured } from "../pricing/unmeasured-sections";

export type WizardStepKind = "info" | "settings";

export const WIZARD_STEPS = [
    { key: "intro", labelKey: "introStep", kind: "info" },
    { key: "pms", labelKey: "pmsConnection", kind: "settings" },
    { key: "analysis", labelKey: "analysis", kind: "settings" },
    // The step key is persisted in `onboarding_profiles.current_step_key`, so it
    // stays put; only the display label tracks the step's widened scope
    // (property type, location, and room-kind structure).
    { key: "propertyClassification", labelKey: "propertyIdentity", kind: "settings", section: "propertyStructure", group: "foundation" },
    // The setup choice: switch Optivo on with recommended settings, have
    // Optivo's team set the property up, or walk the simplified guide (which
    // also asks the goal). It follows both steps above, because switching on
    // finalizes from this screen instead of walking on, and finalize needs what
    // they produce: the property location, which only Property Identity
    // assigns and which the route refuses without, and the analysis, which is
    // where the room kinds and their base prices come from — without it the
    // seeded strategy knows of no room to price. A request to Optivo's team
    // arrives with the property connected and analysed for the same reason.
    // The key predates the screen and is persisted in `current_step_key`, so
    // it stays put while the label says what the screen now asks.
    { key: "typology", labelKey: "setupChoiceStep", kind: "info", group: "foundation" },
    // The archetype gate stood after the picker, and a profile-summary screen
    // after that. Both are gone: the setup choice states the goal outright and
    // shows what it means as it is chosen, so there was nothing left to score
    // or to confirm. The posture leads the pricing steps: the setup choice
    // picked the goal, and this is where it is seen and changed before any
    // number is set against it. Essential on both surfaces — the goal and the
    // reactivity are decisions every property makes — with the dials
    // underneath them behind the step's own reveal.
    { key: "strategyPosture", labelKey: "strategyPosture", kind: "settings", section: "strategyPosture", expertFields: true, group: "foundation" },
    // The market step owns the weather dial as well as the comp set one, and
    // weather is free, on by default, and already moving this property's rates,
    // so the step shows even without competitor tracking.
    { key: "marketInfluence", labelKey: "marketInfluenceStep", kind: "settings", section: "marketInfluence", tier: "expert", group: "foundation" },
    // The weekday spread comes before the price band: Key Metrics sets the
    // minimum and maximum a room type may reach, and the spread is stated
    // against the base price, so a band chosen first is chosen without knowing
    // how far Friday and Saturday already push. It still precedes the calendar
    // exceptions further down, which override it rather than stack on it.
    { key: "dayAdjustments", labelKey: "dayAdjustments", kind: "settings", section: "dayAdjustments", expertFields: true, group: "prices" },
    { key: "keyMetrics", labelKey: "keyMetricsStep", kind: "settings", section: "keyMetrics", expertFields: true, group: "prices" },
    { key: "propertyStructure", labelKey: "propertyStructure", kind: "settings", section: "propertyStructure", expertFields: true, group: "prices" },
    { key: "bookingWindow", labelKey: "bookingWindowStep", kind: "settings", section: "propertyStructure", tier: "expert", group: "prices" },
    // Rounding ships at the currency's sensible step and every generated price
    // already lands on it, so the essential path prices correctly without ever
    // asking.
    { key: "priceGranularity", labelKey: "priceGranularity", kind: "settings", section: "priceGranularity", tier: "expert", group: "prices" },
    { key: "rateRestrictions", labelKey: "rateRestrictions", kind: "settings", section: "rateRestrictions", tier: "expert", group: "sellingRules" },
    { key: "lastMinute", labelKey: "lastMinuteStep", kind: "settings", section: "lastMinute", tier: "expert", group: "sellingRules" },
    { key: "gapFilling", labelKey: "gapFilling", kind: "settings", section: "lastMinute", tier: "expert", group: "sellingRules" },
    // The analysis already derives a month-by-month shape from the property's
    // own history and every night is priced by its month, so an untouched
    // seasonality is a working one rather than a missing decision.
    // Expert for a property whose seasons were measured; asked of one whose
    // were not — see `isSettingVisible`.
    { key: "seasonality", labelKey: "seasonClassification", kind: "settings", section: "seasonality", tier: "expert", essentialWhenUnmeasured: true, group: "calendar" },
    { key: "specialDates", labelKey: "specialDatesStep", kind: "settings", section: "specialDates", group: "calendar" },
    { key: "bridgeDays", labelKey: "bridgeDays", kind: "settings", section: "specialDates", tier: "expert", group: "calendar" },
    { key: "review", labelKey: "reviewActivate", kind: "info" },
] as const satisfies readonly {
    key: string;
    labelKey: string;
    kind: WizardStepKind;
    section?: StrategySectionId;
    tier?: SettingsTier;
    expertFields?: boolean;
    essentialWhenUnmeasured?: boolean;
    group?: StrategySectionGroup;
}[];

export type WizardStepKey = (typeof WIZARD_STEPS)[number]["key"];

export interface WizardStepDefinition {
    key: WizardStepKey;
    labelKey: string;
    kind: WizardStepKind;
    section?: StrategySectionId;
    /** Omitted means essential — see `settings-tier.ts`. */
    tier?: SettingsTier;
    /** Whether the step hides expert-tier fields behind its own reveal. */
    expertFields?: boolean;
    /**
     * Expert-tier, but asked on the essential path of a property the analysis
     * has not measured this section for — see `isSettingVisible`.
     */
    essentialWhenUnmeasured?: boolean;
    /**
     * The group the app menu lists the step under — the one the strategy
     * editor files the section it edits under (`strategy-section-groups.ts`).
     * Omitted for the steps around the pricing ones: the introduction, the
     * PMS connection, the analysis and the review.
     */
    group?: StrategySectionGroup;
}

/**
 * Resolve a step's index by its stable key. Render logic keys off this rather
 * than hard-coded numeric literals, so inserting or reordering steps stays
 * correct in one place.
 */
export function stepIndexByKey(key: WizardStepKey): number {
    return WIZARD_STEPS.findIndex((s) => s.key === key);
}

export function wizardStep(key: WizardStepKey): WizardStepDefinition {
    const step = WIZARD_STEPS.find((s) => s.key === key);
    if (!step) throw new Error(`Unknown wizard step: ${key}`);
    return step;
}

/** What the wizard already knows, and therefore does not need to ask about. */
export interface WizardStepSkipContext {
    /** PMS credentials are missing, or the saved connection fails its health check. */
    pmsSetupRequired: boolean;
    /**
     * Whether the connection has opted into competitor tracking. Reported
     * outward so the sidebar rail can describe the same path the wizard walks.
     */
    compsetEnabled?: boolean;
    /** Expert-tier steps are walked past while this is off. */
    expertMode: boolean;
    /**
     * Sections the analysis did not measure (`unmeasuredSectionsFromAnalysis`).
     * A step marked `essentialWhenUnmeasured` is walked for these even with
     * expert mode off. Absent while the analysis is unknown, which opens nothing.
     */
    unmeasuredSections?: readonly StrategySectionId[];
}

/**
 * The path-shaping facts the wizard reports outward, so the sidebar rail lists
 * the steps the wizard will actually walk rather than every step that exists.
 */
export type WizardPathContext = Pick<WizardStepSkipContext, "unmeasuredSections"> & {
    compsetEnabled: boolean;
};

/** A step the wizard walks past without rendering. */
export function isWizardStepSkipped(key: WizardStepKey, ctx: WizardStepSkipContext): boolean {
    if (key === "pms") return !ctx.pmsSetupRequired;
    // Everything on the expert tier already has a working default, so a run
    // without expert mode is the same strategy with fewer screens.
    // No step is skipped for want of a comp set: the market step owns the
    // weather dial too, which is free, on by default and already moving this
    // property's rates.
    const step: WizardStepDefinition = wizardStep(key);
    return !isSettingVisible(step, ctx.expertMode, isSectionMeasured(step.section, ctx.unmeasuredSections));
}

/** First step at or after `fromIndex` that is not skipped. */
export function nextVisibleStep(fromIndex: number, ctx: WizardStepSkipContext): number {
    const last = WIZARD_STEPS.length - 1;
    let index = Math.min(Math.max(fromIndex, 0), last);
    while (index < last && isWizardStepSkipped(WIZARD_STEPS[index].key, ctx)) index++;
    return index;
}

/** First step at or before `fromIndex` that is not skipped. */
export function previousVisibleStep(fromIndex: number, ctx: WizardStepSkipContext): number {
    let index = Math.min(fromIndex, WIZARD_STEPS.length - 1);
    while (index > 0 && isWizardStepSkipped(WIZARD_STEPS[index].key, ctx)) index--;
    return index;
}

/**
 * Question screens that were steps of their own before their single question
 * moved inline, mapped to the step that now carries it. A profile saved while
 * one of them was on screen still names it in `current_step_key`, so resume has
 * to land somewhere real rather than falling back to a numeric index that means
 * something different now that the model is six steps shorter.
 */
const RETIRED_STEP_KEYS: Record<string, WizardStepKey> = {
    q_marketInfluence: "marketInfluence",
    q_keyMetrics: "keyMetrics",
    q_priceGranularity: "priceGranularity",
    q_rateRestrictions: "rateRestrictions",
    q_lastMinute: "lastMinute",
    q_dayAdjustments: "dayAdjustments",
    // The posture's own question screen is gone — the step it fed asks its
    // three cards outright — so a profile saved on it resumes on that step.
    q_strategyPosture: "strategyPosture",
    // The archetype gate and the profile-summary screen that followed it are
    // gone too: the setup choice does both jobs, so a profile saved on either
    // resumes at the step that now follows them.
    typologySummary: "strategyPosture",
};

/** Resolve a stored step key to a step that still exists, or undefined. */
export function resolveStoredStepKey(key: string | null | undefined): WizardStepKey | undefined {
    if (!key) return undefined;
    if (WIZARD_STEPS.some((step) => step.key === key)) return key as WizardStepKey;
    return RETIRED_STEP_KEYS[key];
}

/** What a resuming profile has already assigned, beyond the step it stopped on. */
export interface ResumePrerequisites {
    /** Whether `verifiedAssumptions.marketLocation` holds a location. */
    hasPropertyLocation: boolean;
}

/**
 * Where a profile actually picks back up, once what it has assigned is taken
 * into account as well as where it stopped.
 *
 * One case needs this, and only because the profile screen moved. It used to
 * stand before the analysis and Property Identity, so a profile saved on it
 * carries a step key that now names a screen two steps further along than that
 * run ever reached. Landing there would put "Switch on and go" — the one way
 * on that finalizes on the spot rather than continuing — in front of an operator
 * with no property location, and `POST /api/onboarding/finalize` refuses
 * without one. Property Identity is where the location is assigned, so that is
 * where such a profile resumes; every other step is left where it is.
 */
export function resumeStepKeyForProfile(
    key: WizardStepKey,
    prerequisites: ResumePrerequisites,
): WizardStepKey {
    if (key === "typology" && !prerequisites.hasPropertyLocation) return "propertyClassification";
    return key;
}

/**
 * The questions this run will actually be asked, in wizard order.
 *
 * Whether the questionnaire counts as finished has to be judged against this
 * rather than the whole question bank: an essential run never walks the
 * expert-tier steps, and the competitor question needs a comp set, so counting
 * every question would file a run that answered everything it was shown as
 * permanently `questionnaire_partial`.
 */
export function pathQuestionIds(ctx: WizardStepSkipContext): string[] {
    const ids: string[] = [];
    for (const step of WIZARD_STEPS as readonly WizardStepDefinition[]) {
        if (isWizardStepSkipped(step.key, ctx)) continue;
        const stepQuestions = step.section ? strategySection(step.section).questionIds : [];
        if (stepQuestions.length === 0) continue;
        // The market step shows for its weather dial even with no comp set; a
        // question about it would be meaningless without one.
        if (step.section === "marketInfluence" && ctx.compsetEnabled !== true) continue;
        for (const id of stepQuestions) {
            if (!ids.includes(id)) ids.push(id);
        }
    }
    return ids;
}

export function sectionForSectionQuestion(questionId: string): StrategySectionId | undefined {
    return sectionForQuestion(questionId);
}

/** Sections that carry questions, in wizard order — used by the editor prompts. */
export const SECTIONS_WITH_QUESTIONS: readonly StrategySectionId[] = STRATEGY_SECTION_REGISTRY
    .filter((s) => s.questionIds.length > 0)
    .map((s) => s.id);


/**
 * Whether the wizard offers expert mode at all.
 *
 * Switched off for now (29 Sep 2026): operators met the advanced steps and the
 * per-step "I'm an expert" reveals in the middle of their first setup and did
 * not know what to do with them. Every one of those settings ships with a
 * working default and stays in the strategy editor, which keeps its own expert
 * mode. While this is false the wizard shows no expert switch, walks the
 * essential path whatever the stored preference says, and holds every step on
 * the essential tier (`ExpertModeAvailability`). Setting it back to true
 * restores all of it — the rail follows through `wizardExpertMode`.
 */
export const WIZARD_EXPERT_MODE_AVAILABLE: boolean = false;

/**
 * The expert mode the wizard's path is walked with: the stored preference,
 * where the wizard offers expert mode at all. The wizard and the sidebar rail
 * both read it through here, so the rail lists the steps the wizard walks.
 */
export function wizardExpertMode(
    storedPreference: boolean,
    available: boolean = WIZARD_EXPERT_MODE_AVAILABLE,
): boolean {
    return available && storedPreference;
}

/**
 * How many settings steps the simplified guide walks after the setup choice —
 * counted off the path this run will actually take, so an unmeasured section
 * the guide has to ask is counted and a skipped step is not. The review at the
 * end is not a settings step and is left out.
 */
export function countGuideSteps(ctx: WizardStepSkipContext): number {
    const choice = stepIndexByKey("typology");
    return (WIZARD_STEPS as readonly WizardStepDefinition[])
        .filter((step, index) => index > choice && step.kind === "settings")
        .filter((step) => !isWizardStepSkipped(step.key, ctx))
        .length;
}

/** Steps only an operator in expert mode is walked through. */
export function isExpertWizardStep(key: WizardStepKey): boolean {
    return wizardStep(key).tier === "expert";
}

/** How many steps switching expert mode off would hide, for the toggle's hint. */
export function countExpertWizardSteps(ctx: Omit<WizardStepSkipContext, "expertMode">): number {
    return (WIZARD_STEPS as readonly WizardStepDefinition[])
        .filter((step) => step.tier === "expert")
        .filter((step) => !isWizardStepSkipped(step.key, { ...ctx, expertMode: true }))
        .length;
}
